import React from 'react';

/**
 * LAS ETIQUETAS ROJAS DE LAS INTERVENCIONES YA REALIZADAS (administración,
 * 14/10/2026): «Cirugía — dd/mm/aaaa» y «Retoque — dd/mm/aaaa».
 *
 * Las calcula el servidor (services/intervenciones_realizadas.py) con UNA
 * función para todo: cada cita las trae en `cirugias_previas` y la ficha en
 * `intervenciones_realizadas`, las dos como `[{tipo, date}]`. Nacen al marcar
 * una cirugía o un retoque «En recuperación» o «Se fue» y se acumulan: aquí
 * solo se pintan, en orden de fecha, sin decidir nada.
 *
 * Este fichero es lo común a la agenda, al resumen de la cita y a la cabecera
 * de la ficha: el texto, el orden, el título que las lista todas y la lista
 * entera (`EtiquetasIntervenciones`). La rejilla de la agenda, donde no
 * siempre caben, las pliega a «la más reciente + N» (AgendaGrid.jsx).
 */

/** "2099-03-15" -> "15/03/2099" sin depender de la zona horaria. */
export function fechaCortaIntervencion(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(iso);
}

/** «Cirugía — 15/03/2099» */
export function textoIntervencion(qx) {
  return `${qx?.tipo || 'Cirugía'} — ${fechaCortaIntervencion(qx?.date)}`;
}

/** Las válidas, por fecha (la más antigua primero), como las da el servidor. */
export function ordenarIntervenciones(lista) {
  return (Array.isArray(lista) ? lista : [])
    .filter((qx) => qx && qx.date)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date))
      || String(a.tipo || '').localeCompare(String(b.tipo || '')));
}

/** El título que las dice todas (lo que se lee al pasar el ratón). */
export function tituloIntervenciones(lista) {
  const ordenadas = ordenarIntervenciones(lista);
  if (!ordenadas.length) return '';
  return `Intervenciones realizadas: ${ordenadas.map(textoIntervencion).join(' · ')}`;
}

/**
 * La cita que devuelve una acción (editarla, moverla…) sustituye a la de la
 * lista hasta que llega la recarga. Las respuestas de los estados del día
 * traen las intervenciones del paciente; la de editar o mover, no: sin esto
 * desaparecían un momento de la agenda y del resumen. Se conservan las que
 * había SOLO si sigue siendo el mismo paciente: las de otro no se le pegan ni
 * un instante.
 */
export function conservarIntervenciones(anterior, actualizada) {
  if (!anterior || !actualizada || 'cirugias_previas' in actualizada) return actualizada;
  if (!('cirugias_previas' in anterior)) return actualizada;
  if (!anterior.patient_id || anterior.patient_id !== actualizada.patient_id) return actualizada;
  return { ...actualizada, cirugias_previas: anterior.cirugias_previas };
}

// LA FORMA de la etiqueta, sin sus colores ni cómo se coloca: la comparten
// las de las intervenciones (en rojo) y las de las marcas de paciente (en el
// color de la marca, EtiquetasMarcasPaciente.jsx; administración, 21/10/2026).
export const FORMA_ETIQUETA = 'rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide leading-none';
// Un punto más, para la cabecera de la ficha.
export const FORMA_ETIQUETA_GRANDE = 'rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide leading-tight';
// El rojo de siempre de la agenda (bg-red-600, letras blancas, mayúsculas).
export const CLASE_ETIQUETA_INTERVENCION = `inline-flex items-center ${FORMA_ETIQUETA} bg-red-600 text-white whitespace-nowrap`;
const CLASE_ETIQUETA_GRANDE = `inline-flex items-center ${FORMA_ETIQUETA_GRANDE} bg-red-600 text-white whitespace-nowrap`;

/**
 * Todas, una detrás de otra y pasando a la línea siguiente si no caben (en el
 * móvil, sin scroll de lado). Para el resumen de la cita y la cabecera de la
 * ficha, donde hay sitio para todas. Sin ninguna, nada.
 *  - `grande`: un punto más, para la cabecera de la ficha.
 */
export default function EtiquetasIntervenciones({ intervenciones, testId = 'intervenciones-realizadas', className = '', grande = false }) {
  const lista = ordenarIntervenciones(intervenciones);
  if (!lista.length) return null;
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-1 min-w-0 max-w-full ${className}`}
      data-testid={testId}
      title={tituloIntervenciones(lista)}
      role="list"
      aria-label="Intervenciones realizadas"
    >
      {lista.map((qx) => (
        <span
          key={`${qx.tipo}-${qx.date}`}
          role="listitem"
          className={grande ? CLASE_ETIQUETA_GRANDE : CLASE_ETIQUETA_INTERVENCION}
          data-testid={`${testId}-item`}
        >
          {textoIntervencion(qx)}
        </span>
      ))}
    </span>
  );
}
